import React, { useEffect, useRef, useMemo, useState } from 'react';
import settingsService from '../services/settingsService';

const ENV = {
  adminEnabled: process.env.REACT_APP_ADMIN_ADSENSE_ENABLED,
  adminClient: process.env.REACT_APP_ADMIN_ADSENSE_CLIENT,
  adminSlot: process.env.REACT_APP_ADMIN_ADSENSE_SLOT,
  adminTestMode: process.env.REACT_APP_ADMIN_ADSENSE_TEST_MODE,
};

const toBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const normalizeClientId = (value) => {
  const raw = (value || '').toString().trim();
  if (!raw) return '';
  if (raw.startsWith('ca-pub-')) return raw;
  const digitsOnly = raw.replace(/[^0-9]/g, '');
  return digitsOnly ? `ca-pub-${digitsOnly}` : raw;
};

const ensureAdSenseScript = (client) => {
  if (!client) return Promise.resolve(false);
  const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  if (document.querySelector(`script[src="${src}"]`)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
};

/**
 * Full-width AdSense banner rendered below the Footer.
 * Height is determined by Google. Shows a labelled placeholder in development.
 */
const SidebarAdSense = () => {
  const adRef = useRef(null);
  const [serverSettings, setServerSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    settingsService.getPublicSettings('general')
      .then((res) => {
        if (mounted) setServerSettings(res?.success ? (res.settings || {}) : {});
      })
      .catch(() => {
        if (mounted) setServerSettings({});
      });
    return () => { mounted = false; };
  }, []);

  const config = useMemo(() => {
    const s = serverSettings || {};
    return {
      enabled: toBoolean(s.admin_adsense_enabled, toBoolean(ENV.adminEnabled, false)),
      client: normalizeClientId(s.admin_adsense_client || ENV.adminClient || ''),
      slot: (s.admin_adsense_slot || ENV.adminSlot || '').toString().trim(),
      testMode: toBoolean(s.admin_adsense_test_mode, toBoolean(ENV.adminTestMode, false)),
    };
  }, [serverSettings]);

  const shouldRender = !!(config.enabled && config.client && config.slot);

  useEffect(() => {
    if (!shouldRender || !adRef.current) return;
    let disposed = false;
    ensureAdSenseScript(config.client).then((loaded) => {
      if (!loaded || disposed || !adRef.current) return;
      if (adRef.current.dataset.adsbygoogleStatus === 'done') return;
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (_) { /* ignore duplicate push errors */ }
    });
    return () => { disposed = true; };
  }, [shouldRender, config.client, config.slot]);

  const isDev = process.env.NODE_ENV === 'development';

  if (!shouldRender) {
    if (!isDev) return null;
    return (
      <div className="w-full bg-gray-100 border-t border-dashed border-gray-300 py-4 flex items-center justify-center">
        <span className="text-xs text-gray-400 font-semibold tracking-widest uppercase">
          Ad Space — AdSense not configured
        </span>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-100 border-t border-gray-200">
      {isDev && (
        <div className="text-center text-xs text-gray-400 font-semibold tracking-widest uppercase py-1">
          Ad Space (localhost — no real ads)
        </div>
      )}
      <ins
        key={`footer-ad-${config.client}-${config.slot}-${config.testMode ? 'test' : 'live'}`}
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={config.client}
        data-ad-slot={config.slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
        data-adtest={config.testMode ? 'on' : 'off'}
      />
    </div>
  );
};

export default SidebarAdSense;
